import { useNavigate } from "react-router-dom";
import { Box, Flex, Heading, Text, Stack, HStack, Icon, Badge } from "@chakra-ui/react";
import { ChevronRight, Flag, MapPin } from "lucide-react";
import { statusMeta } from "@/lib/submissionMeta";
import { displayStatus } from "@/lib/urgent";
import { colors } from "@/theme/colors";

const H1_SIZE = { base: "2xl", md: "3xl" };
const SUB_SIZE = { base: "sm", md: "md" };
const TILE_HOVER = { borderColor: colors.teal, boxShadow: "0 2px 10px rgba(37,161,176,0.12)" };
const ROW_HOVER = { borderColor: colors.teal, bg: colors.tealTint };

export const timeGreeting = () => {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
};

export const Greeting = ({ name, sub }) => (
  <Stack gap="1" mb="6">
    <Heading fontFamily="heading" fontWeight="700" fontSize={H1_SIZE} color={colors.textPrimary} data-testid="home-greeting">
      {timeGreeting()}{name ? `, ${name.split(" ")[0]}` : ""}
    </Heading>
    <Text color={colors.textSecondary} fontSize={SUB_SIZE}>{sub}</Text>
  </Stack>
);

// Clickable summary number — routes into the feature (pre-filtered where useful).
// unavailable=true: the load FAILED — show "—" (unknown), never an authoritative 0.
export const StatTile = ({ icon, label, value, sub, accent, to, testId, unavailable }) => {
  const navigate = useNavigate();
  return (
    <Flex as="button" type="button" onClick={() => navigate(to)} data-testid={testId}
      direction="column" align="flex-start" textAlign="left" bg={colors.white} border="1px solid"
      borderColor={colors.border} borderRadius="14px" p="4" w="full"
      opacity={unavailable ? 0.65 : 1}
      _hover={TILE_HOVER}
      transition="border-color 0.15s, box-shadow 0.15s">
      <HStack gap="2" mb="2">
        <Flex align="center" justify="center" w="32px" h="32px" borderRadius="9px" bg={accent.bg}>
          <Icon as={icon} boxSize="4" color={accent.fg} />
        </Flex>
        <Text fontSize="xs" color={colors.textSecondary}>{label}</Text>
      </HStack>
      <Heading fontFamily="heading" fontWeight="700" fontSize="2xl" color={unavailable ? colors.textSecondary : colors.textPrimary}>
        {unavailable ? "—" : value}
      </Heading>
      <Text fontSize="xs" color={colors.textSecondary} mt="0.5">{unavailable ? "couldn't load — no saved data yet" : sub}</Text>
    </Flex>
  );
};

export const QuickLink = ({ icon, label, desc, to, testId, accent = colors.teal }) => {
  const navigate = useNavigate();
  return (
    <Flex as="button" type="button" onClick={() => navigate(to)} data-testid={testId}
      w="full" textAlign="left" align="center" gap="3" bg={colors.white} border="1px solid"
      borderColor={colors.border} borderRadius="12px" px="4" py="3.5"
      _hover={{ borderColor: accent, bg: colors.tealTint }} transition="border-color 0.15s, background-color 0.15s">
      <Icon as={icon} boxSize="5" color={accent} flexShrink={0} />
      <Box flex="1">
        <Text fontWeight="600" fontSize="sm" color={colors.textPrimary}>{label}</Text>
        {desc && <Text fontSize="xs" color={colors.textSecondary}>{desc}</Text>}
      </Box>
      <Icon as={ChevronRight} boxSize="4" color={colors.textSecondary} />
    </Flex>
  );
};

export const HomeCard = ({ title, action, children, testId }) => (
  <Box bg={colors.white} border="1px solid" borderColor={colors.border} borderRadius="14px" p="5" data-testid={testId}>
    {(title || action) && (
      <Flex justify="space-between" align="center" mb="3">
        <Text fontWeight="700" fontSize="sm" color={colors.textPrimary}>{title}</Text>
        {action}
      </Flex>
    )}
    {children}
  </Box>
);

export const EmptyState = ({ children }) => (
  <Flex align="center" justify="center" minH="110px" borderRadius="12px" border="1px dashed"
    borderColor={colors.border} bg={colors.page} px="4" textAlign="center">
    <Text color={colors.textSecondary} fontSize="sm">{children}</Text>
  </Flex>
);

// Recent submissions list. onOpen present -> rows are clickable (reviewers).
export const RecentSubs = ({ subs, onOpen, companyOf }) => (
  <Stack gap="2">
    {subs.map((s) => {
      const meta = statusMeta(displayStatus(s));
      const inner = (
        <>
          <Box flex="1" minW="150px">
            <HStack gap="1.5" flexWrap="wrap">
              <Text fontSize="sm" fontWeight="600" color={colors.textPrimary}>{s.template_title}</Text>
              {s.revision > 1 && <Badge bg={colors.tealTint} color={colors.tealHover} borderRadius="full" px="2" fontSize="10px">Rev {s.revision}</Badge>}
              {s.flagged && <Icon as={Flag} boxSize="3.5" color={colors.poppy} />}
              {companyOf && <Badge bg={colors.cream} color={colors.textSecondary} borderRadius="full" px="2" fontSize="10px" textTransform="none">{companyOf(s)}</Badge>}
            </HStack>
            <HStack gap="1" color={colors.textSecondary} flexWrap="wrap">
              <Text fontSize="xs">{s.worker_name}</Text>
              <Icon as={MapPin} boxSize="3" />
              <Text fontSize="xs">{s.location_name}</Text>
              <Text fontSize="xs">· {new Date(s.created_at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}</Text>
            </HStack>
          </Box>
          <Badge bg={meta.bg} color={meta.color} borderRadius="full" px="2.5">{meta.label}</Badge>
        </>
      );
      const common = {
        "data-testid": `home-sub-${s.id}`, align: "center", gap: "3", flexWrap: "wrap",
        px: "3.5", py: "2.5", borderRadius: "10px", border: "1px solid", borderColor: colors.border,
      };
      return onOpen ? (
        <Flex key={s.id} as="button" type="button" textAlign="left" w="full" onClick={() => onOpen(s.id)}
          _hover={ROW_HOVER} transition="border-color 0.15s, background-color 0.15s"
          {...common}>{inner}</Flex>
      ) : (
        <Flex key={s.id} {...common}>{inner}</Flex>
      );
    })}
  </Stack>
);
